import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { X, ChevronDown, ChevronUp, Shield } from "lucide-react";

const COOKIE_KEY = "veranda_cookies_consent";

function getConsent() {
  try {
    const raw = localStorage.getItem(COOKIE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch { return null; }
}

function saveConsent(consent) {
  localStorage.setItem(COOKIE_KEY, JSON.stringify({ ...consent, date: new Date().toISOString() }));
}

export default function CookieBanner() {
  const [visible, setVisible] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [analytics, setAnalytics] = useState(false);
  const [marketing, setMarketing] = useState(false);

  useEffect(() => {
    const consent = getConsent();
    if (!consent) {
      const timer = setTimeout(() => setVisible(true), 1500);
      return () => clearTimeout(timer);
    }
  }, []);

  const handleAcceptAll = () => {
    saveConsent({ necessary: true, analytics: true, marketing: true });
    setVisible(false);
  };

  const handleRejectAll = () => {
    saveConsent({ necessary: true, analytics: false, marketing: false });
    setVisible(false);
  };

  const handleSaveCustom = () => {
    saveConsent({ necessary: true, analytics, marketing });
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <div
      data-testid="cookie-banner"
      className="fixed bottom-0 left-0 right-0 z-[9998] p-4 sm:p-6 animate-slideUp"
    >
      <div className="max-w-2xl mx-auto bg-[#051F16] border border-[#1D4133] rounded-sm shadow-2xl shadow-black/50 overflow-hidden">
        {/* Main content */}
        <div className="p-5 sm:p-6">
          <div className="flex items-start gap-3 mb-4">
            <Shield className="w-5 h-5 text-[#EAB308] mt-0.5 flex-shrink-0" />
            <div>
              <h3 className="text-[#F0FDF4] font-semibold text-sm mb-1">Soubory cookies</h3>
              <p className="text-[#94A3B8] text-xs leading-relaxed">
                Tento web používá cookies pro správné fungování a zlepšení vašeho zážitku.
                Můžete si vybrat, které kategorie povolíte.
              </p>
            </div>
          </div>

          {/* Details toggle */}
          <button
            onClick={() => setShowDetails(!showDetails)}
            className="flex items-center gap-1.5 text-xs text-[#EAB308] hover:text-yellow-300 transition-colors mb-4"
            data-testid="cookie-details-toggle"
          >
            {showDetails ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
            {showDetails ? "Skrýt nastavení" : "Podrobné nastavení"}
          </button>

          {/* Detail settings */}
          {showDetails && (
            <div className="space-y-3 mb-5 pl-1" data-testid="cookie-details">
              {/* Necessary */}
              <div className="flex items-center justify-between py-2 border-b border-[#1D4133]/50">
                <div>
                  <p className="text-[#F0FDF4] text-sm font-medium">Nezbytné</p>
                  <p className="text-[#94A3B8] text-xs">Přihlášení, zabezpečení, základní funkce</p>
                </div>
                <Switch checked disabled className="data-[state=checked]:bg-[#1D4133] opacity-60 cursor-not-allowed" />
              </div>

              {/* Analytics */}
              <div className="flex items-center justify-between py-2 border-b border-[#1D4133]/50">
                <div>
                  <p className="text-[#F0FDF4] text-sm font-medium">Analytické</p>
                  <p className="text-[#94A3B8] text-xs">Pomáhají nám zlepšovat web</p>
                </div>
                <Switch
                  checked={analytics}
                  onCheckedChange={setAnalytics}
                  data-testid="cookie-analytics-switch"
                  className="data-[state=checked]:bg-[#EAB308]"
                />
              </div>

              {/* Marketing */}
              <div className="flex items-center justify-between py-2">
                <div>
                  <p className="text-[#F0FDF4] text-sm font-medium">Marketingové</p>
                  <p className="text-[#94A3B8] text-xs">Personalizovaný obsah a reklamy</p>
                </div>
                <Switch
                  checked={marketing}
                  onCheckedChange={setMarketing}
                  data-testid="cookie-marketing-switch"
                  className="data-[state=checked]:bg-[#EAB308]"
                />
              </div>
            </div>
          )}

          {/* Buttons */}
          <div className="flex flex-col sm:flex-row gap-2">
            {showDetails ? (
              <Button
                onClick={handleSaveCustom}
                data-testid="cookie-save-btn"
                className="flex-1 bg-[#EAB308] text-[#02120D] hover:bg-yellow-500 rounded-sm font-semibold text-xs uppercase tracking-wider h-9"
              >
                Uložit nastavení
              </Button>
            ) : (
              <Button
                onClick={handleAcceptAll}
                data-testid="cookie-accept-btn"
                className="flex-1 bg-[#EAB308] text-[#02120D] hover:bg-yellow-500 rounded-sm font-semibold text-xs uppercase tracking-wider h-9"
              >
                Přijmout vše
              </Button>
            )}
            <Button
              onClick={handleRejectAll}
              variant="outline"
              data-testid="cookie-reject-btn"
              className="flex-1 border-[#1D4133] text-[#94A3B8] hover:bg-[#0B2A20] hover:text-[#F0FDF4] rounded-sm text-xs uppercase tracking-wider h-9"
            >
              Jen nezbytné
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
